import { FadeIn, FadeInStagger, FadeInStaggerItem } from '../../components/ui/AnimatedSection';
import { useTranslation } from 'react-i18next';

export default function Features() {
  const { t } = useTranslation();
  const features = t('home.features.items', { returnObjects: true }) as Array<{
    title: string;
    titleItalic: string;
    desc: string;
  }>;

  return (
    <section className="py-24 md:py-40 bg-black-deep text-white relative border-t border-white/5" id="promesse">
      {/* Background subtil */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gold-900/10 rounded-full blur-[150px] translate-x-1/3 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gold-900/5 rounded-full blur-[150px] -translate-x-1/2 translate-y-1/3"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24">
          
          {/* LEFT COLUMN: STICKY HEADER */}
          <div className="w-full lg:w-4/12">
            <div className="lg:sticky lg:top-40">
              <FadeIn>
                <span className="text-gold-400 tracking-[0.4em] text-xs font-light uppercase mb-8 block flex items-center">
                  <span className="w-8 h-px bg-gold-500 mr-4"></span>
                  {t('home.features.tag')}
                </span>
                <h2 className="text-5xl md:text-6xl lg:text-7xl font-serif text-white leading-tight font-light tracking-wide mb-8">
                  {t('home.features.title1')} <br/>
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-600 font-serif italic">{t('home.features.title2')}</span>
                </h2>
                
                <p className="text-gray-400 font-light text-lg leading-relaxed max-w-sm mb-12">
                  {t('home.features.desc')}
                </p>
                
                {/* Scroll indicator (desktop only) */}
                <div className="hidden lg:flex items-center space-x-4 text-gray-500 font-light text-xs uppercase tracking-widest">
                  <div className="w-px h-12 bg-gradient-to-b from-gold-500 to-transparent"></div>
                  <span>{t('home.features.scroll')}</span>
                </div>
              </FadeIn>
            </div>
          </div>

          {/* RIGHT COLUMN: SCROLLING CARDS */}
          <div className="w-full lg:w-8/12">
            <FadeInStagger className="space-y-8 md:space-y-10">
              {features.map((feature, index) => (
                <FadeInStaggerItem key={index} className="group cursor-pointer">
                  <div className="relative overflow-hidden bg-gradient-to-br from-[#111111] to-[#080808] border border-white/5 p-10 md:p-14 rounded-2xl transition-all duration-700 hover:border-gold-500/30 hover:shadow-[0_20px_60px_rgba(201,152,85,0.05)] hover:-translate-y-1">
                    
                    {/* Hover Glow on background */}
                    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,rgba(201,152,85,0.1)_0%,transparent_50%)] opacity-0 group-hover:opacity-100 transition-opacity duration-1000 pointer-events-none"></div>

                    {/* Giant Watermark Number */}
                    <div className="absolute -bottom-16 -right-10 text-[16rem] font-serif font-bold text-white/[0.02] select-none pointer-events-none group-hover:text-gold-500/[0.04] transition-colors duration-1000 leading-none">
                      0{index + 1}
                    </div>

                    <div className="relative z-10 flex flex-col md:flex-row md:items-start gap-8">
                      {/* Typographic Number */}
                      <div className="text-gold-500/50 font-serif text-3xl font-light pt-2 group-hover:text-gold-400 transition-colors duration-500">
                        0{index + 1}.
                      </div>
                      
                      <div>
                        <h3 className="text-3xl md:text-4xl font-serif text-white mb-6 group-hover:text-gold-200 transition-colors duration-500 tracking-wide">
                          {feature.title}
                          <span className="font-serif italic text-gold-500/80 group-hover:text-gold-400">{feature.titleItalic}</span>
                        </h3>
                        
                        <p className="text-gray-400 font-light leading-relaxed text-lg md:text-xl max-w-2xl">
                          {feature.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                </FadeInStaggerItem>
              ))}
            </FadeInStagger>
          </div>

        </div>
      </div>
    </section>
  );
}
